<script setup lang="ts">
import { isEqual } from "lodash-es";
import { computed, ref, watch } from "vue";

import { getBoardDiff } from "@/backend/read/BackendReader";
import SidePanelContent from "@/components/SidePanel/SidePanelContent.vue";
import ComplexList from "@/components/ui/ComplexList/ComplexList.vue";
import ListEmpty from "@/components/ui/ComplexList/ListEmpty.vue";
import LoadingIndicator from "@/components/ui/LoadingIndicator.vue";
import { runMinDelay, useSyncedTask } from "@/composables/useSyncedTask";
import type { ChangeTypeItem, StickyChange } from "@/model/change";
import { useActivityStore } from "@/store/activity";
import { useBoardStore } from "@/store/board";
import type { DateItem } from "@/store/helpers/activityDateFilters";
import { fadeOutAllCardMarks, resetAllCardMarks, resetCardMark } from "@/store/helpers/mark";

import ChangeTypesFilter from "./internal/ChangeTypesFilter.vue";
import DiffsBySticky from "./internal/DiffsBySticky.vue";

const changeTypes = computed({
  get: () => store?.selectedChangeTypes,
  set: (values: ChangeTypeItem[]) => store.selectChangeTypes(values),
});

const store = useActivityStore();
const changes = ref<StickyChange[]>([]);
const { loading, runSync } = useSyncedTask();

watch(
  () => ({ ...store.allFilters }),
  (newState, prevState) => {
    // somehow the watch triggers sometimes even if neither board nor date change
    // check it to avoid multiple calls to the backend
    if (!isEqual(newState, prevState)) {
      void loadData(newState.date, newState.changeTypes);
    }
  },
  { immediate: true },
);

async function loadData(date: DateItem, changeTypes: string[]) {
  const filters = useActivityStore().serverFilters;
  const loadEvents = getBoardDiff(
    useBoardStore().currentBoard().id,
    date.fromDate,
    date.toDate,
    filters,
    [...changeTypes, "!a_center", "!stack_pos"],
  );
  // wait until ToggleButton finishes transition to not disturb the animation
  await runSync(
    () => runMinDelay(loadEvents, 300),
    (events) => {
      changes.value = events;
      updateHighlightedCards();
    },
  );
}

/**
 * Highlight the cards that match the current filters
 */
function updateHighlightedCards() {
  const board = useBoardStore().board;

  if (board && store.board && store.selectedDate && !loading.value) {
    fadeOutAllCardMarks(board);
    for (const change of changes.value) {
      resetCardMark(board.cards[change.stickyId]);
    }
  } else if (board) {
    resetAllCardMarks(board);
  }
}
</script>

<template>
  <SidePanelContent v-focus-trap v-autofocus>
    <template #sub-title>
      <div class="panel-header">
        <h2 class="subtitle">{{ $t("boardDiff.compare.title") }}</h2>
        <p class="description">{{ $t("boardDiff.compare.description") }}</p>
        <ChangeTypesFilter v-model="changeTypes" />
      </div>
    </template>

    <ComplexList>
      <DiffsBySticky :changes />

      <ListEmpty v-if="loading">
        <LoadingIndicator />
      </ListEmpty>

      <ListEmpty v-if="changes.length === 0 && !loading">
        {{ $t("searchResults.noResults") }}
      </ListEmpty>
    </ComplexList>
  </SidePanelContent>
</template>
